import React, { useState, useEffect } from 'react';
import { Home, Search, Eye, Plus, Edit2, Trash2, Printer } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '../../hooks/useCurrency';
import { tenantInvoiceService } from '../../lib/tenant-services';
import { useTenant } from '../../contexts/TenantContext';
import type { Invoice } from '../../lib/supabase';

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { tenantId, loading: tenantLoading, error: tenantError } = useTenant();
  const { formatCurrency } = useCurrency();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadInvoices();
  }, [statusFilter, startDate, endDate]);

  const loadInvoices = async () => {
    if (!tenantId) return;
    
    try {
      setLoading(true);
      const data = await tenantInvoiceService.getAll(tenantId);
      setInvoices(data || []);
    } catch (error) {
      console.error('Error loading invoices:', error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const getMockInvoices = (): Invoice[] => {
    return [
      {
        id: '1',
        patient_id: '1',
        invoice_number: 'INV-2025-001',
        issue_date: '2025-01-27',
        due_date: '2025-02-27',
        subtotal: 5000,
        discount_amount: 500,
        tax_rate: 0,
        tax_amount: 0,
        total: 4500,
        amount_paid: 4500,
        balance_due: 0,
        status: 'paid',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        patient: {
          id: '1',
          name: 'John Doe',
          unique_id: 'P-001',
          phone: '01712345678',
          email: 'john@example.com',
          date_of_birth: '1990-01-01',
          address: 'Dhaka',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      },
      {
        id: '2',
        patient_id: '2',
        invoice_number: 'INV-2025-002',
        issue_date: '2025-01-26',
        due_date: '2025-02-26',
        subtotal: 3000,
        discount_amount: 0,
        tax_rate: 0,
        tax_amount: 0,
        total: 3000,
        amount_paid: 1000,
        balance_due: 2000,
        status: 'partial',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        patient: {
          id: '2',
          name: 'Jane Smith',
          unique_id: 'P-002',
          phone: '01798765432',
          email: 'jane@example.com',
          date_of_birth: '1985-05-15',
          address: 'Chittagong',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      },
    ];
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = 
      invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.patient?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.patient?.unique_id?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleDeleteInvoice = async (invoiceId: string) => {
    if (!tenantId) {
      alert('Tenant information not available');
      return;
    }
    
    try {
      await tenantInvoiceService.delete(tenantId, invoiceId);
      alert('Invoice deleted successfully');
      loadInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('Failed to delete invoice');
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: { [key: string]: string } = {
      paid: 'bg-green-100 text-green-800',
      partial: 'bg-yellow-100 text-yellow-800',
      overdue: 'bg-red-100 text-red-800',
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800',
      unpaid: 'bg-red-100 text-red-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  if (tenantLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (tenantError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
          <p className="text-gray-600">{tenantError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Home className="w-4 h-4" />
            <span>/</span>
            <span>Billings/Reception</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Invoices</span>
          </div>
          <button
            onClick={() => navigate('/billings/invoices/new')}
            className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>New Invoice</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-none px-6 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/billings/invoices/new')}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
            >
              + New Invoice
            </button>
            <input
              type="text"
              placeholder="Invoice No."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              placeholder="From date"
              className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              placeholder="To date"
              className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <button
              onClick={loadInvoices}
              className="px-6 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
            >
              Search
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Invoice Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Invoice No</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Patient</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Date Of Birth</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Total Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Discount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Paid</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Refer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Note</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Created by</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Delivery Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={13} className="px-4 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="px-4 py-4 text-center text-gray-500">
                      No invoices found
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {new Date(invoice.issue_date).toLocaleDateString('en-GB')}
                        <div className="text-xs text-gray-500">{invoice.time || '06:00 AM'}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">
                        {invoice.invoice_number}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <div className="font-medium">{invoice.patient?.name}</div>
                        <div className="text-xs text-gray-500">{invoice.patient?.unique_id || invoice.patient?.phone}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {invoice.patient?.date_of_birth ? (
                          <>
                            <div>{new Date(invoice.patient.date_of_birth).toLocaleDateString('en-GB').split('/')[0]}</div>
                            <div className="text-xs">Age: {new Date().getFullYear() - new Date(invoice.patient.date_of_birth).getFullYear()} years</div>
                          </>
                        ) : 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-green-600">
                        {formatCurrency(invoice.total || 0)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <div className="text-red-600">Dis: {formatCurrency(invoice.discount_amount || 0)} TL</div>
                        <div className="text-green-600 font-semibold">Total: {formatCurrency(invoice.total || 0)} TL</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <div className="text-green-600 font-semibold">Paid: {formatCurrency(invoice.amount_paid || 0)}</div>
                        <div className="text-red-600">Due: {formatCurrency(invoice.balance_due || 0)}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                          invoice.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                          invoice.status === 'unpaid' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {invoice.status === 'unpaid' ? 'Unpaid' : invoice.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {invoice.ref_doctor?.name || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                        {invoice.note || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {invoice.created_by || 'Admin'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {invoice.delivery_date ? new Date(invoice.delivery_date).toLocaleDateString('en-GB') : '-'}
                        <div className="text-xs">{invoice.time || '06:00 AM'}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/billings/invoices/${invoice.id}`)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/billings/invoices/${invoice.id}/edit`)}
                            className="text-green-600 hover:text-green-900"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/billings/invoices/${invoice.id}/print`)}
                            className="text-purple-600 hover:text-purple-900"
                            title="Print"
                          >
                            <Printer className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (confirm('Are you sure you want to delete this invoice?')) {
                                handleDeleteInvoice(invoice.id);
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
